/**
 * Copyright 2024-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useState } from "react";
import { useUpdateEffect } from "react-use";
import { validate as isValidUUID } from "uuid";
import { Item, SubItem } from "./types";

type Props = {
  selectableItems: Item[];
  selectedOptions: string[];
  onMultiSelect: (ids: string[]) => void;
  selectedSubOptions?: string[];
  onMultiSubSelect?: (ids: string[]) => void;
};

export function useSelectableListFilter({
  selectableItems,
  selectedOptions,
  onMultiSelect,
  selectedSubOptions,
  onMultiSubSelect,
}: Props) {
  const [selectedItems, setSelectedItems] = useState(selectedOptions);
  const [selectedSubItems, setSelectedSubItems] = useState(
    selectedSubOptions ?? []
  );
  const [search, setSearch] = useState("");
  const [filteredOptions, setFilteredOptions] = useState<Item[]>([]);

  useUpdateEffect(() => {
    setSelectedItems(selectedOptions);
  }, [JSON.stringify(selectedOptions)]);

  useUpdateEffect(() => {
    onMultiSelect(selectedItems ?? []);
  }, [selectedItems]);

  useUpdateEffect(() => {
    setSelectedSubItems(selectedSubOptions ?? []);
  }, [JSON.stringify(selectedSubOptions ?? [])]);

  useUpdateEffect(() => {
    onMultiSubSelect?.(selectedSubItems ?? []);
  }, [selectedSubItems]);

  useEffect(() => {
    setFilteredOptions(
      selectableItems
        .map((item) => {
          return {
            ...item,
            subCheckboxes: item.subCheckboxes?.filter(({ label, value }) => {
              // search by value only if valid uuid, like valid plan id
              if (isValidUUID(search)) {
                return (
                  label?.toLowerCase().includes(search) ||
                  value?.toLowerCase().includes(search)
                );
              }
              return label?.toLowerCase().includes(search.toLowerCase());
            }),
          };
        })
        .filter(({ label, value, subCheckboxes }) => {
          // search by value only if valid uuid, like valid plan id
          if (isValidUUID(search)) {
            return (
              label?.toLowerCase().includes(search) ||
              value?.toLowerCase().includes(search)
            );
          }
          return (
            label?.toLowerCase().includes(search.toLowerCase()) ||
            (subCheckboxes && subCheckboxes.length > 0)
          );
        })
    );
  }, [search, selectableItems]);

  const onApply = (item: string, allSubItems: SubItem[]) => {
    const allSubValues = allSubItems
      .filter((subItem) => !!subItem)
      .map((subItem) => subItem.value);
    const itemClearedZeros = item.replace(/^0+/, "");
    const isItemSelected =
      selectedItems.includes(item) || selectedItems.includes(itemClearedZeros);

    if (isItemSelected) {
      setSelectedItems(removeItemFromState(item, itemClearedZeros));
      setSelectedSubItems((prev) =>
        prev.filter((storedSubItem) => !allSubValues.includes(storedSubItem))
      );
    } else {
      setSelectedItems((prev) => [...prev, item]);
      setSelectedSubItems((prev) => {
        const removedDuplicates = new Set([
          ...prev,
          ...(allSubValues as string[]),
        ]);

        return Array.from(removedDuplicates);
      });
    }
  };

  const onSubItemApply = (
    item: string,
    parentItem: string,
    allSubItems: SubItem[]
  ) => {
    const allSubValues = allSubItems.map((subItem) => subItem.value);
    const itemClearedZeros = replaceZeros(item);
    const parentItemClearedZeros = replaceZeros(parentItem);
    const isSubItemSelected =
      selectedSubItems.includes(item) ||
      selectedSubItems.includes(itemClearedZeros);

    if (isSubItemSelected) {
      setSelectedSubItems(removeItemFromState(item, itemClearedZeros));
      setSelectedItems(removeItemFromState(parentItem, parentItemClearedZeros));
    } else {
      setSelectedSubItems((prev) => [...prev, item]);
      // check if all subItems are selected
      const updatedSelectedSubItems = [...selectedSubItems, item];
      const selectedSubItemsOfItem = allSubValues.filter((subItem) =>
        updatedSelectedSubItems.includes(subItem ?? "")
      );

      if (selectedSubItemsOfItem.length === allSubItems.length) {
        setSelectedItems((prev) => [...prev, parentItem]);
      }
    }
  };

  return {
    selectedItems,
    selectedSubItems,
    filteredOptions,
    setSearch,
    onApply,
    onSubItemApply,
  };
}

function replaceZeros(item: string) {
  return item.replace(/^0+/, "");
}

function removeItemFromState(item: string, itemClearedZeros: string) {
  return (prev: string[]) =>
    prev.filter(
      (storedItem) => storedItem !== item && storedItem !== itemClearedZeros
    );
}
